import { v4 as uuid } from 'uuid'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { administrationApi, type RoleCreationCommand, type RoleCreationResult } from '@/services/instanceAdministration'
import type { ApiError } from '@/services/http/errors'
import { ADMIN_DATA } from './useAccess'

export type RoleInput = Pick<RoleCreationCommand, 'source_instance_id' | 'display_name' | 'avatar_key'>

/** Own creation identity separately from selection; a lost response retries the same server operation. */
export const useRoles = (userId: string, revision: number, onAccessFailure: () => void) => {
  const client = useQueryClient()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const live = useRef(true)
  const commands = useRef(new Map<string, RoleCreationCommand>())
  const active = useRef<{ key: string; controller: AbortController; promise: Promise<RoleCreationResult> } | null>(null)
  useEffect(() => {
    live.current = true
    return () => { live.current = false; active.current?.controller.abort() }
  }, [])
  const create = useCallback((input: RoleInput): Promise<RoleCreationResult> => {
    const key = JSON.stringify(input)
    if (active.current?.key === key) return active.current.promise
    if (active.current) return Promise.reject(new Error('正在创建角色'))
    const command = commands.current.get(key) ?? { ...input, request_id: uuid(), expected_catalog_revision: revision }
    commands.current.set(key, command)
    const controller = new AbortController()
    setPending(true); setError(null)
    const promise = administrationApi.create(command, controller.signal).then(async (accepted) => {
      controller.signal.throwIfAborted()
      await client.invalidateQueries({ queryKey: [...ADMIN_DATA, userId, 'instances'] })
      controller.signal.throwIfAborted()
      commands.current.delete(key)
      return accepted
    }).catch((failure: Error) => {
      if (live.current && !controller.signal.aborted) setError(failure)
      const status = (failure as ApiError).status
      if (live.current && [401, 403].includes(status ?? 0)) onAccessFailure()
      if (status === 409) {
        commands.current.delete(key)
        void client.invalidateQueries({ queryKey: [...ADMIN_DATA, userId, 'instances'] })
      }
      throw failure
    }).finally(() => {
      active.current = null
      if (live.current) setPending(false)
    })
    active.current = { key, controller, promise }
    return promise
  }, [client, onAccessFailure, revision, userId])
  return { create, pending, error }
}
